/* ═══════════════════════════════════════════════════════════════════════════
   WORK LOG WIZARD                                 [work-log-wizard-2026-09-20]

   Spec: ~/Library/CloudStorage/OneDrive-Personal/App/WORK_LOG_WIZARD_SPEC_2026-09-20.md
   Prototype: https://claude.ai/artifact/4kJ7vykofWuxAjdcooL9X1

   Replaces AttemptSheet's single scrolling form with one question per screen.
   Ships behind `work_log_wizard` (see useWorkLogWizard in server-app.jsx);
   AttemptSheet is untouched and stays the default until this proves out.

   THREE AXES decide what the server sees:
     job type       -> which question set   (jobs.service_type)
     outcome        -> which branch         (the server's first tap)
     jurisdiction   -> which legal rules    (derived from the address)
   None is ever asked as a preference. Only the outcome is a choice, because
   only the outcome is a fact the server alone knows.

   ┌─────────────────────────────────────────────────────────────────────────┐
   │ READ THIS BEFORE EDITING — the one way to break the build                │
   │                                                                          │
   │ This module imports from server-app.jsx, which imports this module back. │
   │ The cycle is SAFE ONLY because every reference to an imported binding    │
   │ happens inside a function body.                                          │
   │                                                                          │
   │   const OUTCOMES = [{ color: C.ok }];   // ✗ TypeError at module init    │
   │   const outcomes = () => [{ color: C.ok }];  // ✓ evaluated on call      │
   │                                                                          │
   │ `C` is `const C = {}` mutated in place by applyTheme, and `api` is a     │
   │ module-scope literal. esbuild hoists both, so a bundle-scope read gets   │
   │ `undefined` — sometimes silently (blank colors), sometimes as a crash.   │
   │ `node --check` will NOT catch it. Only a real bundle + load will.        │
   │                                                                          │
   │ Every list below is therefore a FUNCTION, not a const array.             │
   └─────────────────────────────────────────────────────────────────────────┘
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef } from "react";
import {
  CheckCircle2, User, XCircle, Home, Bell, MapPinOff, ShieldAlert,
  MoreHorizontal, Camera, Upload, ChevronLeft, X, MapPin, AlertTriangle,
} from "lucide-react";
import {
  C, api, Sheet, jobDisplay, ssUploadEvidence, ssFileToBytes,
  ssMailOriginDefault, ssTodayLocalISO, ssIsoToUsDate,
  updateJobLiveActivity, endJobLiveActivity,
} from "./server-app.jsx";

/* ── job type ──────────────────────────────────────────────────────────────
   Maps jobs.service_type (free-ish text from the catalog) onto a question
   set. Serving is the default: an unknown type must never land somewhere
   that asks the wrong questions, and serving is the one set that is fully
   specified and legally gated today. */
function jobTypeOf(job) {
  const t = String((job && (job.serviceType || job.type)) || "").toLowerCase();
  if (/skip/.test(t)) return "skiptrace";
  if (/court.?filing|e.?file|filing/.test(t)) return "file";
  if (/retriev|records?.?request/.test(t)) return "retrieve";
  if (/writ/.test(t)) return "writ";
  if (/\bsos\b|secretary.of.state/.test(t)) return "sos";
  return "serve";
}

/* ── outcomes, per type ────────────────────────────────────────────────────
   Wire codes are the backend's existing enum (server.js ~10423). Anything not
   in that enum is rejected with a 400, so this table is the contract.

   NOT-YET-SUPPORTED TYPES fall back to the serving set deliberately. The
   backend has no `type` discriminator yet (patch queued for when cle-backend
   unlocks), so a filing outcome posted today would be silently dropped. Until
   that lands, only `serve` is reachable — jobTypeOf returning "file" still
   renders serving questions rather than collecting data that goes nowhere. */
function outcomesFor(type) {
  const serve = [
    { id: "served",     wire: "served-personal",         t: "Served",      s: "Personal, in hand",    c: C.ok,     icon: CheckCircle2 },
    { id: "sub_served", wire: "served-substitute-home",  t: "Sub-served",  s: "Left with another",    c: C.royal,  icon: User },
    { id: "refused",    wire: "refused",                 t: "Refused",     s: "Identified, refused",  c: C.err,    icon: XCircle },
    { id: "not_home",   wire: "not-home",                t: "Not home",    s: "Nobody there",         c: C.warn,   icon: Home },
    { id: "no_answer",  wire: "no-answer",               t: "No answer",   s: "Signs of life",        c: C.warn,   icon: Bell },
    { id: "bad_addr",   wire: "not-served",              t: "Bad address", s: "Moved / wrong",        c: C.err,    icon: MapPinOff },
    { id: "unsafe",     wire: "other",                   t: "Unsafe",      s: "Withdrew",             c: C.purple, icon: ShieldAlert },
    { id: "other",      wire: "other",                   t: "Other",       s: "Explain in notes",     c: C.steel,  icon: MoreHorizontal },
  ];
  return serve; // see the note above — other types land here until the backend patch ships
}

function idMethods() {
  return ["Verbal confirmation", "Photo ID shown", "Recognised from photo",
          "Named on mailbox/door", "Confirmed by co-resident", "Other"];
}
function relationships() {
  return ["Spouse/partner", "Co-resident", "Family member", "Co-worker", "Manager/agent", "Other"];
}
/* POS-010 item 5b. The operator's tap decides the printed box — see the spec's
   note about the result code currently overriding it server-side. */
function subLocations() {
  return [
    { id: "home",    t: "Their home", s: "Dwelling or usual abode — item 5b(2)" },
    { id: "business", t: "Their work", s: "Usual place of business — item 5b(1)" },
    { id: "unknown", t: "Neither",    s: "Usual mailing address — item 5b(3)" },
  ];
}
/* Required elements of CCP §415.20(b), plus §415.21. Unticked by default and
   never inferred: each prints as a sworn statement. */
function swornElements() {
  return [
    { id: "isAdult",  t: "They were at least 18 years old",
      s: "Required element — Code Civ. Proc. § 415.20(b)" },
    { id: "informed", t: "I told them the general nature of the papers",
      s: "Required element — Code Civ. Proc. § 415.20(b)" },
    { id: "presence", t: "I left the papers in their presence",
      s: "Code Civ. Proc. § 415.21 — where entry was refused" },
  ];
}
function descRows() {
  return [
    ["gender", "Sex",        ["Male", "Female"]],
    ["race",   "Race",       ["White", "Black", "Hispanic", "Asian", "Middle Eastern", "Other"]],
    ["age",    "Approx age", ["Under 18", "18-25", "26-34", "35-45", "46-60", "60+"]],
    ["height", "Height",     ["Under 5'4\"", "5'4\"-5'9\"", "5'10\"-6'0\"", "6'1\"-6'4\"", "Over 6'4\""]],
    ["weight", "Weight",     ["Under 120 lb", "120-150 lb", "150-180 lb", "180-200 lb", "Over 200 lb"]],
    ["hair",   "Hair",       ["Black", "Brown", "Blonde", "Grey", "Red", "None / bald"]],
  ];
}
function featureChips() {
  return ["Tattoos", "Beard", "Moustache", "Scar", "Piercing", "Cane/walker", "Uniform", "Glasses"];
}
function signsOfLife() {
  return ["Lights on", "Vehicle present", "TV/radio audible", "Mail collected",
          "Dog barking", "Curtains moved", "None"];
}
function talkedToOpts()  { return ["Neighbour", "Manager", "Passerby", "Nobody"]; }
function badAddrOpts()   { return ["Vacant", "Forwarding order", "Name unknown here", "Demolished", "Wrong number"]; }
function unsafeOpts()    { return ["Aggressive person", "Weapon seen", "Dog off leash",
                                   "Gated / no access", "Unsafe structure", "Withdrew on instinct"]; }

/* ── draft persistence ─────────────────────────────────────────────────────
   A tap on the sheet backdrop unmounts the component and takes every field
   with it. Keyed PER JOB, because a server carries many active jobs and
   half-finished drafts are normal.

   Never persists base64 or a photo without an s3Key: the registration wizard
   learned both the hard way (quota death, and a restored slot that looks
   complete but whose bytes the backend then rejects). */
const DRAFT_PREFIX = "ss_worklog_draft_v1:";
const DRAFT_MAX = 8;

function draftKey(jobId) { return DRAFT_PREFIX + String(jobId); }

function loadDraft(jobId) {
  try {
    const raw = localStorage.getItem(draftKey(jobId));
    if (!raw) return null;
    const d = JSON.parse(raw);
    return (d && typeof d === "object" && d.v === 1) ? d : null;
  } catch (_) { return null; }
}
function saveDraft(jobId, state) {
  try {
    const slim = { ...state, v: 1, ts: Date.now() };
    // s3Key-bearing references only. No bytes, ever.
    slim.photos = (state.photos || []).filter(p => p && p.key)
      .map(p => ({ key: p.key, fileUrl: p.fileUrl || null, kind: p.kind || "camera" }));
    localStorage.setItem(draftKey(jobId), JSON.stringify(slim));
    evictDrafts();
  } catch (_) { /* quota or blocked storage — the wizard still works, just not resumable */ }
}
function clearDraft(jobId) {
  try { localStorage.removeItem(draftKey(jobId)); } catch (_) {}
}
/* Without eviction the per-job keys reintroduce the quota failure by a slower
   route. Oldest-first by the ts every draft carries. */
function evictDrafts() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf(DRAFT_PREFIX) === 0) {
        let ts = 0;
        try { ts = (JSON.parse(localStorage.getItem(k)) || {}).ts || 0; } catch (_) {}
        keys.push([k, ts]);
      }
    }
    if (keys.length <= DRAFT_MAX) return;
    keys.sort((a, b) => a[1] - b[1]);
    keys.slice(0, keys.length - DRAFT_MAX).forEach(([k]) => {
      try { localStorage.removeItem(k); } catch (_) {}
    });
  } catch (_) {}
}
/* Called from the logout teardown. A sworn draft must never survive into
   another server's session. */
export function clearAllWorkLogDrafts() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf(DRAFT_PREFIX) === 0) doomed.push(k);
    }
    doomed.forEach(k => { try { localStorage.removeItem(k); } catch (_) {} });
  } catch (_) {}
}

/* ── small presentational pieces ───────────────────────────────────────── */

function Pip({ done, now }) {
  const st = { width: 13, height: 13, borderRadius: 999, flex: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 8, color: "#fff",
    border: "1.5px solid " + (done ? C.ok : now ? C.royal : C.steelL),
    borderWidth: now ? 4 : 1.5,
    background: done ? C.ok : "transparent" };
  return <span style={st}>{done ? "✓" : ""}</span>;
}

function Stepper({ steps, at, labels, onJump }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 6px",
      minHeight: 30, overflowX: "auto", background: C.bgW,
      borderBottom: "1px solid " + C.steelL, scrollbarWidth: "none" }}>
      {steps.map((k, i) => (
        <React.Fragment key={k}>
          {i > 0 && <span style={{ width: 9, height: 1, background: C.steelL, flex: "none" }} />}
          <button type="button" disabled={i > at}
            onClick={() => { if (i <= at) onJump(i); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 7px", fontSize: 10.5, fontWeight: 600,
              whiteSpace: "nowrap", background: "none", border: 0,
              fontFamily: "inherit", cursor: i <= at ? "pointer" : "default",
              opacity: i > at ? 0.55 : 1,
              color: i < at ? C.ok : i === at ? C.royal : C.steel }}>
            <Pip done={i < at} now={i === at} />{labels[k]}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

function Chip({ on, sug, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on ? "true" : "false"}
      style={{
        background: sug && !on ? C.goldXL : C.white,
        border: "2px " + (sug && !on ? "dashed " + C.goldM : "solid " + (on ? C.royal : C.steelL)),
        borderRadius: 9, padding: "10px 13px", fontSize: 13,
        fontWeight: on ? 600 : 500, color: on ? C.royal : C.ink,
        minHeight: 44, cursor: "pointer", fontFamily: "inherit",
        boxShadow: on ? "inset 0 -3px 0 " + C.royal : "none",
      }}>
      {sug && !on && (
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: C.warn, marginRight: 6 }}>on file</span>
      )}
      {children}
    </button>
  );
}

function Check({ on, title, sub, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on ? "true" : "false"}
      style={{ display: "flex", gap: 11, alignItems: "flex-start", width: "100%",
        background: C.white, border: "2px solid " + (on ? C.ok : C.steelL),
        borderRadius: 10, padding: 12, marginBottom: 8, minHeight: 52,
        textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: C.ink }}>
      <span style={{ width: 22, height: 22, borderRadius: 6, flex: "none",
        border: "2px solid " + (on ? C.ok : C.steelL), background: on ? C.ok : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontSize: 14, marginTop: 1 }}>{on ? "✓" : ""}</span>
      <span>
        <span style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35, display: "block" }}>{title}</span>
        {sub && <span style={{ fontSize: 11, color: C.steel, marginTop: 2, display: "block" }}>{sub}</span>}
      </span>
    </button>
  );
}

function Label({ children, required }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em",
      textTransform: "uppercase", color: C.steel, margin: "15px 0 7px",
      display: "flex", gap: 7, alignItems: "baseline", flexWrap: "wrap" }}>
      {children}
      <span style={{ fontSize: 9.5, color: required ? C.err : C.steel,
        opacity: required ? 1 : 0.75 }}>{required ? "REQUIRED" : "OPTIONAL"}</span>
    </div>
  );
}

function Note({ tone, children, cite }) {
  const map = { warn: [C.goldM, C.goldXL], good: [C.ok, C.okL], bad: [C.err, C.errL], info: [C.info, C.infoL] };
  const [line, bg] = map[tone] || map.warn;
  return (
    <div style={{ borderLeft: "3px solid " + line, background: bg, padding: "9px 11px",
      borderRadius: "0 8px 8px 0", fontSize: 11.5, color: C.steel, margin: "11px 0" }}>
      {children}
      {cite && <span style={{ display: "block", marginTop: 5, fontSize: 10,
        color: C.steel, fontFamily: "Menlo, monospace" }}>{cite}</span>}
    </div>
  );
}

function Tile({ on, color, title, sub, wide, icon: Ico, onClick }) {
  const sel = on ? (color || C.royal) : C.steelL;
  return (
    <button type="button" onClick={onClick} aria-pressed={on ? "true" : "false"}
      style={{ background: C.white, border: "2px solid " + sel, borderRadius: 11,
        padding: "13px 11px", textAlign: "left", cursor: "pointer",
        fontFamily: "inherit", color: C.ink, minHeight: 62, width: wide ? "100%" : undefined,
        marginBottom: wide ? 9 : 0, display: "flex", flexDirection: "column",
        justifyContent: "center", gap: 2,
        boxShadow: on ? "inset 3px 0 0 " + sel : "none" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
        {Ico && <Ico size={16} color={on ? sel : C.steel} />}
        <span style={{ fontWeight: 600, fontSize: 14.5, color: on ? sel : C.ink }}>{title}</span>
      </span>
      {sub && <span style={{ fontSize: 11, color: C.steel }}>{sub}</span>}
    </button>
  );
}

/* ═══ the wizard ═══════════════════════════════════════════════════════════ */

export function WorkLogWizard({ job, onClose, seedOutcome }) {
  const jobId = job && job.id;
  const type = jobTypeOf(job);

  const [step, setStep] = useState(0);
  const [outcome, setOutcome] = useState(seedOutcome || null);
  const [recip, setRecip] = useState("");
  const [relationship, setRelationship] = useState("");
  const [idv, setIdv] = useState([]);
  const [elems, setElems] = useState({});
  const [subLoc, setSubLoc] = useState("");
  const [mailStatus, setMailStatus] = useState("");
  const [mailDateIso, setMailDateIso] = useState("");
  const [mailCity, setMailCity] = useState("");
  const [mailState, setMailState] = useState("CA");
  const [desc, setDesc] = useState({});
  const [features, setFeatures] = useState([]);
  const [signs, setSigns] = useState([]);
  const [plate, setPlate] = useState("");
  const [talked, setTalked] = useState("");
  const [talkedName, setTalkedName] = useState("");
  const [indicators, setIndicators] = useState([]);
  const [unsafe, setUnsafe] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [sworn, setSworn] = useState(false);

  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsErr, setGpsErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const hydrated = useRef(false);
  const fileRef = useRef(null);

  /* Mailing origin is the one thing the old sheet already preloaded well. */
  useEffect(() => {
    const o = ssMailOriginDefault(job, null) || {};
    setMailCity(o.city || "");
    setMailState(o.state || "CA");
  }, [jobId]);

  /* Restore a draft once. */
  useEffect(() => {
    if (hydrated.current || !jobId) return;
    hydrated.current = true;
    const d = loadDraft(jobId);
    if (!d) return;
    if (d.outcome) setOutcome(d.outcome);
    if (d.recip) setRecip(d.recip);
    if (d.relationship) setRelationship(d.relationship);
    if (Array.isArray(d.idv)) setIdv(d.idv);
    if (d.elems) setElems(d.elems);
    if (d.subLoc) setSubLoc(d.subLoc);
    if (d.mailStatus) setMailStatus(d.mailStatus);
    if (d.mailDateIso) setMailDateIso(d.mailDateIso);
    if (d.mailCity) setMailCity(d.mailCity);
    if (d.mailState) setMailState(d.mailState);
    if (d.desc) setDesc(d.desc);
    if (Array.isArray(d.features)) setFeatures(d.features);
    if (Array.isArray(d.signs)) setSigns(d.signs);
    if (d.plate) setPlate(d.plate);
    if (d.talked) setTalked(d.talked);
    if (d.talkedName) setTalkedName(d.talkedName);
    if (Array.isArray(d.indicators)) setIndicators(d.indicators);
    if (Array.isArray(d.unsafe)) setUnsafe(d.unsafe);
    if (Array.isArray(d.photos)) setPhotos(d.photos);
    if (d.notes) setNotes(d.notes);
    if (typeof d.step === "number") setStep(d.step);
    setSavedAt("restored");
  }, [jobId]);

  /* Autosave. Never persists `sworn` — an affirmation is made in the moment,
     not restored from storage. */
  useEffect(() => {
    if (!hydrated.current || !jobId || !outcome) return;
    saveDraft(jobId, { step, outcome, recip, relationship, idv, elems, subLoc,
      mailStatus, mailDateIso, mailCity, mailState, desc, features, signs, plate,
      talked, talkedName, indicators, unsafe, photos, notes });
    try {
      setSavedAt(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
    } catch (_) { setSavedAt("saved"); }
  }, [step, outcome, recip, relationship, idv, elems, subLoc, mailStatus,
      mailDateIso, mailCity, mailState, desc, features, signs, plate, talked,
      talkedName, indicators, unsafe, photos, notes, jobId]);

  /* GPS on open. NEVER fabricated: a manufactured coordinate that the backend
     then HMAC-seals is a false document. Failure leaves gps null and says so. */
  useEffect(() => {
    let dead = false;
    const cap = typeof window !== "undefined" && window.Capacitor;
    const Geo = cap && cap.Plugins && cap.Plugins.Geolocation;
    setGpsBusy(true); setGpsErr("");
    const ok = (lat, lng, acc) => {
      if (dead) return;
      setGps({ lat: Number(lat), lng: Number(lng), accuracy: Number(acc || 0) });
      setGpsBusy(false);
    };
    const bad = (m) => { if (!dead) { setGpsErr(m || "Location unavailable"); setGpsBusy(false); } };
    if (Geo && cap.isNativePlatform && cap.isNativePlatform()) {
      Geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
        .then(p => ok(p.coords.latitude, p.coords.longitude, p.coords.accuracy))
        .catch(e => bad(e && e.message));
    } else if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p => ok(p.coords.latitude, p.coords.longitude, p.coords.accuracy),
        e => bad(e && e.message),
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
    } else { bad("Location not available on this device"); }
    return () => { dead = true; };
  }, [jobId]);

  const gpsOk = !!(gps && typeof gps.lat === "number" && typeof gps.lng === "number"
                   && !isNaN(gps.lat) && !isNaN(gps.lng));
  const contact = outcome === "served" || outcome === "sub_served" || outcome === "refused";
  const isSub = outcome === "sub_served";

  /* Skip trace is desk work — no address to stand at. Every other type needs a
     fix, and the backend rejects a missing one by design. */
  const gpsRequired = type !== "skiptrace";

  function steps() {
    if (!outcome) return ["outcome"];
    if (isSub) return ["outcome", "recipient", "subloc", "mailing", "sworn", "describe", "evidence", "review"];
    if (outcome === "served" || outcome === "refused")
      return ["outcome", "recipient", "sworn", "describe", "evidence", "review"];
    if (outcome === "not_home" || outcome === "no_answer") return ["outcome", "observe", "evidence", "review"];
    if (outcome === "bad_addr") return ["outcome", "badaddr", "evidence", "review"];
    if (outcome === "unsafe") return ["outcome", "unsafe", "evidence", "review"];
    return ["outcome", "evidence", "review"];
  }
  const path = steps();
  const cur = path[Math.min(step, path.length - 1)] || "review";
  const labels = { outcome: "Outcome", recipient: "Recipient", subloc: "Where left",
    mailing: "Mailing", sworn: "Sworn", describe: "Describe", observe: "Observed",
    badaddr: "Address", unsafe: "Unsafe", evidence: "Evidence", review: "Review" };

  /* What stops the save, and which screen fixes it. */
  function blocker() {
    if (!outcome) return null;
    if (gpsRequired && !gpsOk) return { m: "No GPS fix — an attempt cannot be saved without one", g: "outcome" };
    if (contact && !idv.length) return { m: "Record how you verified identity", g: "recipient" };
    if (isSub && !String(recip).trim()) return { m: "Name the person you left the papers with", g: "recipient" };
    if (isSub && !subLoc) return { m: "Record where you left the papers", g: "subloc" };
    if (isSub && !mailStatus) return { m: "Record the CCP §415.20(b) follow-up mailing", g: "mailing" };
    if (isSub && mailStatus !== "not_yet") {
      const iso = mailStatus === "today" ? ssTodayLocalISO() : String(mailDateIso || "").trim();
      if (!iso) return { m: "Pick the date the copies were mailed", g: "mailing" };
      if (iso > ssTodayLocalISO()) return { m: "The mailing date cannot be in the future", g: "mailing" };
      if (!String(mailCity).trim()) return { m: "Enter the city the copies were mailed from", g: "mailing" };
      if (!/^[A-Za-z]{2}$/.test(String(mailState).trim()))
        return { m: "Enter the two-letter state the copies were mailed from", g: "mailing" };
    }
    if (outcome === "other" && !String(notes).trim())
      return { m: "Other needs a note explaining what happened", g: "evidence" };
    return null;
  }

  function toggle(list, setList, v) {
    const i = list.indexOf(v);
    if (i >= 0) setList(list.filter(x => x !== v)); else setList([...list, v]);
  }

  async function addPhoto(kind) {
    setPhotoBusy(true); setErr("");
    try {
      const cap = typeof window !== "undefined" && window.Capacitor;
      const Cam = cap && cap.Plugins && cap.Plugins.Camera;
      if (kind === "camera" && Cam && cap.isNativePlatform && cap.isNativePlatform()) {
        const p = await Cam.getPhoto({ quality: 80, resultType: "base64",
          source: "CAMERA", saveToGallery: false });
        const bin = atob(p.base64String);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const name = "attempt-" + jobId + "-" + Date.now() + ".jpg";
        const r = await ssUploadEvidence(api, { bytes, mime: "image/jpeg",
          fileName: name, context: "attempt-photo", jobId });
        setPhotos(prev => [...prev, { key: r.key, fileUrl: r.fileUrl, name,
          ts: new Date().toISOString(), size: bytes.byteLength, kind: "camera" }]);
      } else {
        fileRef.current && fileRef.current.click();
      }
    } catch (e) { setErr((e && e.message) || "Photo capture failed"); }
    finally { setPhotoBusy(false); }
  }

  async function onPickFiles(e) {
    const files = Array.from((e.target && e.target.files) || []);
    e.target.value = "";
    if (!files.length) return;
    setPhotoBusy(true); setErr("");
    const failed = [];
    for (const f of files) {
      try {
        const bytes = await ssFileToBytes(f);
        const r = await ssUploadEvidence(api, { bytes, mime: f.type || "application/octet-stream",
          fileName: f.name, context: "attempt-photo", jobId });
        setPhotos(prev => [...prev, { key: r.key, fileUrl: r.fileUrl, name: f.name,
          ts: new Date().toISOString(), size: f.size || 0, kind: "upload" }]);
      } catch (_) { failed.push(f.name); }
    }
    if (failed.length) setErr(failed.length + " file(s) failed to upload and were NOT saved as evidence");
    setPhotoBusy(false);
  }

  async function submit() {
    const bl = blocker();
    if (bl) { setErr(bl.m); return; }
    if (!sworn) { setErr("Affirm the declaration before saving"); return; }
    setBusy(true); setErr("");
    try {
      const def = outcomesFor(type).find(o => o.id === outcome);
      /* photos MUST travel as an array — the backend derives photoCount from it
         and rejects entries with no s3Key. Sending a bare count is how the iOS
         app persisted zero photos on every attempt. */
      const evid = (photos || []).filter(p => p && p.key).map(p => ({
        name: p.name || ("attempt-" + jobId + "-" + Date.now() + ".jpg"),
        ts: p.ts || new Date().toISOString(),
        size: p.size || 0,
        s3Key: p.key,
        fileUrl: p.fileUrl || null,
        s3: true,
      }));
      const payload = {
        result: (def && def.wire) || "other",
        notes: String(notes || ""),
        photoCount: evid.length,
        photos: evid,
      };
      if (gpsOk) {
        payload.gps = { lat: gps.lat, lng: gps.lng };
        if (typeof gps.accuracy === "number" && !isNaN(gps.accuracy)) payload.gps.accuracy = gps.accuracy;
      }
      if (contact) {
        payload.servedPersonName = String(recip || "").trim() || null;
        if (isSub) {
          payload.subName = String(recip || "").trim() || null;
          payload.subRelation = String(relationship || "").trim() || null;
          payload.locType = subLoc || null;
          payload.locTypeCaptured = !!subLoc;
          if (mailStatus === "not_yet") {
            /* Nulls can never look like a completed mailing — a sentinel string
               would tick "I thereafter mailed" on a mailing that never went out. */
            payload.mailDate = null;
            payload.mailCity = null;
            payload.notes = [String(payload.notes || "").trim(),
              "CCP 415.20(b) follow-up mailing NOT YET SENT as of this attempt. Service is not complete and the 10-day period has not begun. [415.20b-mailing-pending]"
            ].filter(Boolean).join(" — ");
          } else {
            const iso = mailStatus === "today" ? ssTodayLocalISO() : String(mailDateIso || "").trim();
            payload.mailDate = ssIsoToUsDate(iso);
            payload.mailCity = String(mailCity).trim() + ", " + String(mailState).trim().toUpperCase();
          }
        }
      }
      const sd = {};
      if (contact) {
        if (idv.length) sd.identityVerifiedBy = idv;
        if (elems.isAdult) sd.recipientIsAdult = true;
        if (elems.informed) sd.informedOfNature = true;
        if (elems.presence) sd.leftInPresence = true;
        descRows().forEach(([k]) => { if (desc[k]) sd["subject" + k.charAt(0).toUpperCase() + k.slice(1)] = desc[k]; });
        if (features.length) sd.subjectFeatures = features;
      } else {
        if (signs.length) sd.observations = signs;
        if (plate) sd.vehiclePlate = String(plate).toUpperCase();
        if (talked) sd.talkedTo = talked;
        if (talkedName) sd.talkedToName = talkedName;
        if (indicators.length) sd.indicators = indicators;
        if (unsafe.length) sd.unsafeReasons = unsafe;
      }
      if (Object.keys(sd).length) payload.serviceDetails = sd;

      await api.post("/api/jobs/" + jobId + "/attempts", payload);
      clearDraft(jobId);
      try {
        const w = String(payload.result || "");
        if (w.indexOf("served") === 0) endJobLiveActivity(jobId, "Served — proof captured");
        else updateJobLiveActivity(jobId, { stage: "Attempt logged", status: "attempting" });
      } catch (_) {}
      onClose && onClose(true);
    } catch (e) {
      setErr((e && e.message) || "Could not save the attempt");
    } finally { setBusy(false); }
  }

  /* ── screens ─────────────────────────────────────────────────────────── */

  function head(q, sub, law) {
    return (
      <>
        <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.018em",
          margin: "2px 0 3px", color: C.ink }}>{q}</h2>
        {sub && <p style={{ fontSize: 12.5, margin: "0 0 13px",
          color: law ? C.warn : C.steel, fontWeight: law ? 600 : 400 }}>{sub}</p>}
      </>
    );
  }
  const cardStyle = { background: C.white, border: "1px solid " + C.steelL,
    borderRadius: 12, padding: 13, marginBottom: 11 };
  const inputStyle = { width: "100%", padding: "12px 13px", borderRadius: 9,
    border: "2px solid " + C.steelL, background: C.white, color: C.ink,
    fontSize: 14.5, minHeight: 46, fontFamily: "inherit", boxSizing: "border-box" };

  function chipRow(label, opts, val, set, sug, required) {
    return (
      <>
        <Label required={required}>{label}</Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {opts.map(v => (
            <Chip key={v} on={val === v} sug={sug === v && val !== v}
              onClick={() => set(val === v ? "" : v)}>{v}</Chip>
          ))}
        </div>
      </>
    );
  }

  function screen() {
    if (cur === "outcome") return (
      <>
        {head("What happened?", "Everything after this depends on your answer.")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          {outcomesFor(type).map(o => (
            <Tile key={o.id} on={outcome === o.id} color={o.c} title={o.t} sub={o.s} icon={o.icon}
              onClick={() => { setOutcome(o.id); setStep(1); }} />
          ))}
        </div>
      </>
    );

    if (cur === "recipient") return (
      <>
        {head(isSub ? "Who did you leave them with?" : "Who did you serve?",
          isSub ? "A competent adult who lives or works at this address."
                : "The person who physically took the papers.")}
        <div style={cardStyle}>
          <Label required={isSub}>Name</Label>
          <input style={inputStyle} value={recip} autoComplete="off"
            placeholder={isSub ? "Who took the papers" : "Name of the person served"}
            onChange={e => setRecip(e.target.value)} />
          {!isSub && jobDisplay(job).name && recip !== jobDisplay(job).name && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 8 }}>
              <Chip sug on={false} onClick={() => setRecip(jobDisplay(job).name)}>
                {jobDisplay(job).name}
              </Chip>
            </div>
          )}
          {isSub && chipRow("Relationship to the subject", relationships(), relationship, setRelationship, null, false)}
        </div>
        <div style={cardStyle}>
          <Label required>How did you verify who they were?</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {idMethods().map(m => (
              <Chip key={m} on={idv.indexOf(m) >= 0} onClick={() => toggle(idv, setIdv, m)}>{m}</Chip>
            ))}
          </div>
          <Note tone="warn">
            <b style={{ color: C.ink }}>Select every method that applies.</b> This is the
            field most often attacked on a motion to quash.
          </Note>
        </div>
      </>
    );

    if (cur === "subloc") return (
      <>
        {head("Where did you leave them?", "This decides which box is ticked on POS-010 item 5b.", true)}
        {subLocations().map(o => (
          <Tile key={o.id} wide on={subLoc === o.id} color={C.royal} title={o.t} sub={o.s}
            onClick={() => setSubLoc(o.id)} />
        ))}
      </>
    );

    if (cur === "mailing") return (
      <>
        {head("The follow-up mailing",
          "Substituted service is not complete until copies are mailed — CCP §415.20(b).", true)}
        {[["today", "I mailed them today"], ["other", "I mailed them on another date"],
          ["not_yet", "Not mailed yet"]].map(([id, t]) => (
          <Tile key={id} wide on={mailStatus === id} color={C.royal} title={t}
            onClick={() => setMailStatus(id)} />
        ))}
        {mailStatus === "other" && (
          <div style={cardStyle}>
            <Label required>Date mailed</Label>
            <input type="date" style={inputStyle} value={mailDateIso}
              max={ssTodayLocalISO()} onChange={e => setMailDateIso(e.target.value)} />
          </div>
        )}
        {mailStatus && mailStatus !== "not_yet" && (
          <div style={cardStyle}>
            <Label required>Mailed from</Label>
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} value={mailCity} placeholder="City"
                onChange={e => setMailCity(e.target.value)} />
              <input style={{ ...inputStyle, width: 74, textTransform: "uppercase" }}
                value={mailState} maxLength={2} placeholder="ST"
                onChange={e => setMailState(e.target.value.toUpperCase())} />
            </div>
          </div>
        )}
        {mailStatus === "not_yet" && (
          <Note tone="good" cite="Code Civ. Proc. § 415.20(b)">
            <b style={{ color: C.ink }}>Recorded honestly.</b> The attempt saves now and the
            job carries a mailing-due flag. Service is not complete and the 10-day period
            has not begun.
          </Note>
        )}
      </>
    );

    if (cur === "sworn") return (
      <>
        {head("Confirm what you can swear to",
          "Each of these prints as a sworn statement. Tick only what is true.", true)}
        {swornElements()
          .filter(e => e.id !== "presence" || isSub || outcome === "refused")
          .map(e => (
            <Check key={e.id} on={!!elems[e.id]} title={e.t} sub={e.s}
              onClick={() => setElems({ ...elems, [e.id]: !elems[e.id] })} />
          ))}
      </>
    );

    if (cur === "describe") return (
      <>
        {head("Describe them", "All optional. Confirm what matches, correct what doesn't.")}
        <div style={cardStyle}>
          {descRows().map(([k, lbl, opts]) => (
            <React.Fragment key={k}>
              {chipRow(lbl, opts, desc[k] || "", v => setDesc({ ...desc, [k]: v }), null, false)}
            </React.Fragment>
          ))}
        </div>
        <div style={cardStyle}>
          <Label>Distinguishing features</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {featureChips().map(v => (
              <Chip key={v} on={features.indexOf(v) >= 0}
                onClick={() => toggle(features, setFeatures, v)}>{v}</Chip>
            ))}
          </div>
        </div>
      </>
    );

    if (cur === "observe") return (
      <>
        {head("What did you see?", "All optional — log it and move on.")}
        <div style={cardStyle}>
          <Label>Signs of life</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {signsOfLife().map(v => (
              <Chip key={v} on={signs.indexOf(v) >= 0} onClick={() => toggle(signs, setSigns, v)}>{v}</Chip>
            ))}
          </div>
          <Label>Vehicle plate</Label>
          <input style={{ ...inputStyle, fontFamily: "Menlo, monospace", textTransform: "uppercase" }}
            value={plate} maxLength={10} placeholder="Optional"
            onChange={e => setPlate(e.target.value.toUpperCase())} />
        </div>
        <div style={cardStyle}>
          {chipRow("Spoke to anyone?", talkedToOpts(), talked, setTalked, null, false)}
          {talked && talked !== "Nobody" && (
            <>
              <Label>Their name, if given</Label>
              <input style={inputStyle} value={talkedName} placeholder="Optional"
                onChange={e => setTalkedName(e.target.value)} />
            </>
          )}
        </div>
      </>
    );

    if (cur === "badaddr") return (
      <>
        {head("Why is this address wrong?", "This feeds the skip-trace request back to the client.")}
        <div style={cardStyle}>
          <Label>Indicators</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {badAddrOpts().map(v => (
              <Chip key={v} on={indicators.indexOf(v) >= 0}
                onClick={() => toggle(indicators, setIndicators, v)}>{v}</Chip>
            ))}
          </div>
          {chipRow("Who told you?", talkedToOpts(), talked, setTalked, null, false)}
        </div>
      </>
    );

    if (cur === "unsafe") return (
      <>
        {head("What made it unsafe?",
          "Your judgement is the record, and it flags the address for whoever goes next.")}
        <div style={cardStyle}>
          <Label>Reasons</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {unsafeOpts().map(v => (
              <Chip key={v} on={unsafe.indexOf(v) >= 0}
                onClick={() => toggle(unsafe, setUnsafe, v)}>{v}</Chip>
            ))}
          </div>
        </div>
      </>
    );

    if (cur === "evidence") {
      const cam = photos.filter(p => p.kind === "camera").length;
      const up = photos.filter(p => p.kind === "upload").length;
      return (
        <>
          {head("Evidence and notes", "The door, the address, the documents.")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
            <Tile on={false} color={C.ok} title="Take photo" sub="Stamps date, time, GPS"
              icon={Camera} onClick={() => addPhoto("camera")} />
            <Tile on={false} color={C.ok} title="Upload" sub="Photos, scans or PDF"
              icon={Upload} onClick={() => addPhoto("upload")} />
          </div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple
            style={{ display: "none" }} onChange={onPickFiles} />
          {photoBusy && <Note tone="info">Uploading…</Note>}
          {photos.length > 0 && (
            <div style={{ ...cardStyle, marginTop: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", fontSize: 13.5, fontWeight: 600 }}>
                <span>{photos.length} attached</span>
                <button type="button" onClick={() => setPhotos([])}
                  style={{ background: "none", border: 0, color: C.royal, fontSize: 12.5,
                    fontWeight: 600, textDecoration: "underline", cursor: "pointer",
                    fontFamily: "inherit" }}>Remove all</button>
              </div>
              <div style={{ fontSize: 11.5, color: C.steel, marginTop: 3 }}>
                {[cam ? cam + " from camera" : "", up ? up + " uploaded" : ""].filter(Boolean).join(" · ")}
              </div>
            </div>
          )}
          <Note tone="bad">
            <b style={{ color: C.ink }}>Never photograph the person served, and never record
            audio.</b> Photograph the door, the address and the documents.
          </Note>
          <div style={cardStyle}>
            <Label required={outcome === "other"}>Notes</Label>
            <textarea style={{ ...inputStyle, minHeight: 74, resize: "vertical" }}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Anything the record should carry that the fields above don't." />
          </div>
        </>
      );
    }

    /* review */
    const def = outcomesFor(type).find(o => o.id === outcome);
    const rows = [
      ["Outcome", def ? def.t : ""],
      ["Where", (job && job.address) || ""],
      ["GPS", gpsOk ? (Math.abs(gps.lat).toFixed(4) + "°" + (gps.lat >= 0 ? "N" : "S")
        + ", " + Math.abs(gps.lng).toFixed(4) + "°" + (gps.lng >= 0 ? "E" : "W")
        + (gps.accuracy ? " · ±" + Math.round(gps.accuracy) + "m" : "")) : ""],
    ];
    if (contact) {
      rows.push(["Person served", recip]);
      if (isSub) rows.push(["Relationship", relationship]);
      rows.push(["Identity verified by", idv.join(", ")]);
      swornElements().forEach(e => { if (elems[e.id]) rows.push([e.t, "Yes"]); });
    }
    if (isSub) {
      const L = subLocations().find(l => l.id === subLoc);
      rows.push(["Left at", L ? L.t : ""]);
      rows.push(["Mailing", mailStatus === "not_yet" ? "NOT YET MAILED"
        : mailStatus ? ("Mailed " + (mailStatus === "today" ? "today" : (mailDateIso || ""))
            + " from " + mailCity + ", " + mailState) : ""]);
    }
    const d = descRows().map(([k]) => desc[k]).filter(Boolean).join(", ");
    if (d) rows.push(["Description", d]);
    if (features.length) rows.push(["Features", features.join(", ")]);
    if (signs.length) rows.push(["Signs of life", signs.join(", ")]);
    if (plate) rows.push(["Vehicle plate", plate]);
    if (indicators.length) rows.push(["Indicators", indicators.join(", ")]);
    if (unsafe.length) rows.push(["Unsafe reasons", unsafe.join(", ")]);
    if (talked) rows.push(["Spoke to", talked + (talkedName ? " — " + talkedName : "")]);
    rows.push(["Photos", photos.length ? String(photos.length) : ""]);
    if (notes) rows.push(["Notes", notes]);

    return (
      <>
        {head("Read this, then swear to it", "Exactly what goes onto the proof of service.")}
        <div style={{ border: "1px solid " + C.steelL, borderRadius: 11,
          overflow: "hidden", marginBottom: 12, background: C.white }}>
          {rows.map(([k, v], i) => (
            <div key={k + i} style={{ display: "flex", justifyContent: "space-between",
              gap: 12, padding: "9px 12px", fontSize: 13,
              borderBottom: i < rows.length - 1 ? "1px solid " + C.steelXL : "none" }}>
              <span style={{ color: C.steel, flex: "none", maxWidth: "44%" }}>{k}</span>
              <span style={{ fontWeight: v ? 600 : 400, textAlign: "right",
                wordBreak: "break-word", color: v ? C.ink : C.steel,
                fontStyle: v ? "normal" : "italic", opacity: v ? 1 : 0.7 }}>
                {v || "not recorded"}
              </span>
            </div>
          ))}
        </div>
        <div style={{ border: "2px solid " + C.err, background: C.errL,
          borderRadius: 11, padding: 13 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: C.err, marginBottom: 6 }}>
            Declaration under penalty of perjury
          </div>
          <p style={{ margin: "0 0 11px", fontSize: 12, color: C.steel }}>
            I declare under penalty of perjury under the laws of the State of California
            that the foregoing is true and correct.
          </p>
          <button type="button" onClick={() => setSworn(!sworn)} aria-pressed={sworn ? "true" : "false"}
            style={{ width: "100%", minHeight: 48, borderRadius: 10,
              border: "2px solid " + C.err, background: sworn ? C.err : C.white,
              color: sworn ? "#fff" : C.err, fontWeight: 700, fontSize: 14,
              cursor: "pointer", fontFamily: "inherit" }}>
            {sworn ? "Affirmed" : "Tap to affirm"}
          </button>
        </div>
      </>
    );
  }

  const bl = blocker();
  const last = step >= path.length - 1;

  return (
    <Sheet onClose={onClose} small>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
        {/* context line */}
        <div style={{ background: C.deep, color: "#fff", padding: "6px 14px",
          display: "flex", alignItems: "center", gap: 7, fontSize: 11.5, minHeight: 28 }}>
          <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis" }}>{jobDisplay(job).name}</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ fontFamily: "Menlo, monospace", fontSize: 10, opacity: 0.85 }}>
            #{(job && job.jobNumber) || jobId}
          </span>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center",
            gap: 4, fontSize: 10.5, whiteSpace: "nowrap",
            color: gpsOk ? "#8ee0b4" : gpsBusy ? "#d8d8d8" : "#ffb4ab" }}>
            <MapPin size={11} />
            {gpsOk ? "±" + Math.round(gps.accuracy || 0) + "m"
                   : gpsBusy ? "locating" : "no fix"}
          </span>
          <button type="button" onClick={() => onClose && onClose(false)}
            style={{ background: "none", border: 0, color: "#fff", cursor: "pointer",
              padding: 0, marginLeft: 6, display: "flex" }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <Stepper steps={path} at={Math.min(step, path.length - 1)} labels={labels}
          onJump={setStep} />

        <div style={{ background: C.okL, color: C.ok, fontSize: 10, fontWeight: 600,
          padding: "3px 14px", borderBottom: "1px solid " + C.steelL }}>
          {savedAt ? (savedAt === "restored" ? "Draft restored — safe to close"
                     : "Draft saved " + savedAt + " — safe to close")
                   : "Draft saves automatically as you go"}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "13px 16px 18px", background: C.bg }}>
          {screen()}
          {!gpsOk && gpsRequired && cur === "outcome" && (
            <Note tone="bad">
              <b style={{ color: C.ink }}>
                {gpsBusy ? "Getting your location…" : "No GPS fix"}
              </b>
              {!gpsBusy && <> — an attempt cannot be saved without one. {gpsErr}</>}
            </Note>
          )}
          {err && (
            <div style={{ color: C.err, fontSize: 12, fontWeight: 600, marginTop: 10 }}>
              <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {err}
            </div>
          )}
        </div>

        <div style={{ borderTop: "1px solid " + C.steelL, background: C.bgW, padding: "11px 16px" }}>
          {cur === "review" && bl && (
            <div style={{ fontSize: 11.5, color: C.err, fontWeight: 600, marginBottom: 8 }}>
              <button type="button" onClick={() => {
                const i = path.indexOf(bl.g);
                if (i >= 0) setStep(i);
              }} style={{ background: "none", border: 0, padding: 0, font: "inherit",
                fontWeight: 700, color: C.err, textDecoration: "underline",
                cursor: "pointer", textAlign: "left" }}>
                {"⚠ "}{bl.m}
              </button>
            </div>
          )}
          <div style={{ display: "flex", gap: 9 }}>
            {step > 0 && (
              <button type="button" onClick={() => setStep(step - 1)}
                style={{ flex: "0 0 84px", minHeight: 50, borderRadius: 11,
                  border: "2px solid " + C.steelL, background: "transparent",
                  color: C.steel, fontWeight: 700, fontSize: 14.5, cursor: "pointer",
                  fontFamily: "inherit", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 4 }}>
                <ChevronLeft size={16} /> Back
              </button>
            )}
            {cur === "review" ? (
              <button type="button" disabled={!!bl || !sworn || busy} onClick={submit}
                style={{ flex: 1, minHeight: 50, borderRadius: 11, fontWeight: 700,
                  fontSize: 14.5, fontFamily: "inherit",
                  cursor: (bl || !sworn || busy) ? "not-allowed" : "pointer",
                  border: "2px solid " + ((bl || !sworn) ? C.steelL : C.gold),
                  background: (bl || !sworn) ? "transparent" : C.gold,
                  color: (bl || !sworn) ? C.steel : C.ink }}>
                {busy ? "Saving…" : bl ? "Blocked" : sworn ? "Save attempt" : "Affirm to save"}
              </button>
            ) : (
              <button type="button"
                disabled={(cur === "outcome" && !outcome) || photoBusy}
                onClick={() => setStep(Math.min(path.length - 1, step + 1))}
                style={{ flex: 1, minHeight: 50, borderRadius: 11, fontWeight: 700,
                  fontSize: 14.5, fontFamily: "inherit",
                  cursor: (cur === "outcome" && !outcome) ? "not-allowed" : "pointer",
                  border: "2px solid " + ((cur === "outcome" && !outcome) ? C.steelL : C.deep),
                  background: (cur === "outcome" && !outcome) ? "transparent" : C.deep,
                  color: (cur === "outcome" && !outcome) ? C.steel : "#fff" }}>
                {photoBusy ? "Uploading…" : last ? "Review" : "Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Sheet>
  );
}

export default WorkLogWizard;
